import {FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {useGetTireWarehouseQuery, usePatchTireWarehouseMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {GeneralTab} from '../../components/GeneralTab';
import {StorageLocations} from '../../components/StorageLocations';
import {GeneralTabFormType} from '../../types/generalTabFormSchema';

export function Detail() {
  const {id: warehouseId} = useParams();
  const navigate = useNavigate();

  const tireWarehouseDetailQuery = useGetTireWarehouseQuery(
    {warehouseId: warehouseId || ''},
    {skip: !warehouseId}
  );

  const [updateTireWarehouse, updateTireWarehouseStatus] = usePatchTireWarehouseMutation();

  const defaultValues: GeneralTabFormType = {
    name: tireWarehouseDetailQuery.data?.name || '',
    branchId: tireWarehouseDetailQuery.data?.branchId || '',
    storageLocationSeparator: tireWarehouseDetailQuery.data?.storageLocationSeparator || '',
  };

  const handleSubmit: FormSubmitHandler<GeneralTabFormType> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await updateTireWarehouse({body: data, warehouseId: warehouseId || ''})
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireWarehouses);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{
        breadcrumbs: [
          {
            label: i18n.t('entity.tireWarehouses.labels.title'),
            href: settingsRoutes.tireWarehouses,
          },
        ],
        title: i18n.t('entity.tireWarehousesDetail.labels.title', {
          name: tireWarehouseDetailQuery.data?.name,
        }),
      }}
      tabs={[
        {
          queryId: 'general',
          title: i18n.t('general.labels.general'),
          content: (
            <GeneralTab
              onSubmit={handleSubmit}
              defaultValues={defaultValues}
              isLoading={updateTireWarehouseStatus.isLoading}
              isEditing
            />
          ),
        },
        {
          queryId: 'location',
          title: i18n.t('entity.tireWarehouses.labels.storageLocations'),
          content: <StorageLocations tireWarehouseId={warehouseId || ''} />,
        },
      ]}
      isLoading={tireWarehouseDetailQuery.isLoading}
      isError={tireWarehouseDetailQuery.isError}
      data-testid={testIds.settings.tireWarehousesDetail('template')}
    />
  );
}
